import React from 'react';
import Layout from '../components/Layout';

const TermsAndConditions = () => {
  return (
    <Layout>
      <div style={styles.pageContainer}>
        <h1 style={styles.heading}>Terms & Conditions</h1>
        <p style={styles.text}><strong>Last updated:</strong> 05 September 2025</p>

        <p style={styles.text}>
          By accessing and using the Tender Managers platform and any reports you purchase or download,
          you agree to the Terms below. These are in addition to any order-specific terms presented at checkout.
        </p>

        <h2 style={styles.subheading}>1) Eligibility</h2>
        <p style={styles.text}>You must be at least 18 years of age and have the authority to bind your organization.</p>

        <h2 style={styles.subheading}>2) Payments</h2>
        <p style={styles.text}>
          Payments are processed via secure third-party gateways. Prices may be dynamic based on scope and period.
        </p>

        <h2 style={styles.subheading}>3) Reports License & Use</h2>
        <p style={styles.text}>
          “<strong>Reports</strong>” means any files, spreadsheets, data extracts, dashboards, or analyses produced by
          Tender Managers and delivered to you (including updates or re-downloads). Tender Managers grants you a
          <strong> limited, non-exclusive, non-transferable, revocable license</strong> to use the Reports for your
          <strong> internal business purposes</strong> only.
        </p>

        <h3 style={styles.minorHeading}>Permitted</h3>
        <ul style={styles.list}>
          <li>View, analyze, and use the Reports internally within your organization.</li>
          <li>Make reasonable internal copies/backups for compliance and continuity.</li>
        </ul>

        <h3 style={styles.minorHeading}>Restrictions (Very Important)</h3>
        <ul style={styles.list}>
          <li>No external redistribution or public sharing (incl. WhatsApp/Telegram groups, public drives, forums, social media, client deliverables) without a separate written license.</li>
          <li>No reselling, sublicensing, or using Reports to build a competing dataset, database, or service.</li>
          <li>No removal of watermarks, metadata, attribution, or license notices.</li>
          <li>No misrepresentation of the Reports as your own product.</li>
        </ul>
        <p style={styles.text}>
          Need redistribution rights (e.g., for client work, training, media)? Contact us for a separate license.
        </p>

        <h2 style={styles.subheading}>4) Ownership</h2>
        <p style={styles.text}>
          Tender Managers owns all intellectual property in the Reports, including selection, compilation, normalization,
          enrichment, and presentation. Underlying public data remains the property of its original sources.
          No ownership is transferred to you.
        </p>

        <h2 style={styles.subheading}>5) Sources, Accuracy & Use</h2>
        <ul style={styles.list}>
          <li>Reports are compiled from public and third-party sources (e.g., government portals). Tender Managers is not affiliated with those sources.</li>
          <li>Reports are provided <strong>“as is”</strong> for informational purposes only and do not constitute legal, procurement, or financial advice.</li>
          <li>We strive for accuracy but do not guarantee completeness, timeliness, or error-free data. You are responsible for decisions made using the Reports.</li>
        </ul>

        <h2 style={styles.subheading}>6) Confidentiality</h2>
        <p style={styles.text}>
          Treat the Reports and our pricing as Tender Managers’ Confidential Information. You will use reasonable safeguards to prevent unauthorized disclosure.
        </p>

        <h2 style={styles.subheading}>7) Personal Data</h2>
        <p style={styles.text}>
          Reports are not intended to contain personal data. If you believe personal data is present, stop any sharing and notify us at
          {' '}<a href="mailto:info@tendermanagers.com">info@tendermanagers.com</a>. Each party will comply with applicable data-protection laws.
        </p>

        <h2 style={styles.subheading}>8) Delivery, Availability & Refunds</h2>
        <ul style={styles.list}>
          <li>Digital products are delivered via time-limited, authenticated links; please download promptly.</li>
          <li>All sales are final for delivered digital Reports, except in cases of duplicate charges or non-delivery caused by Tender Managers.</li>
        </ul>

        <h2 style={styles.subheading}>9) Support</h2>
        <p style={styles.text}>
          Basic email support for download issues and clarifications is included for 7 days after purchase. Enhancements or custom analyses are separate services.
        </p>

        <h2 style={styles.subheading}>10) Suspension & Termination</h2>
        <p style={styles.text}>
          We may suspend or terminate your license for breach (e.g., unauthorized sharing). Upon termination, you must delete the Reports and any substantial copies.
        </p>

        <h2 style={styles.subheading}>11) Limitation of Liability</h2>
        <p style={styles.text}>
          To the maximum extent permitted by law, Tender Managers is not liable for indirect, incidental, special, or consequential damages.
        </p>

        <h2 style={styles.subheading}>12) Indemnity</h2>
        <p style={styles.text}>
          You agree to indemnify Tender Managers against claims arising from your misuse, unauthorized distribution, or breach of these Terms.
        </p>

        <h2 style={styles.subheading}>13) Governing Law & Venue</h2>
        <p style={styles.text}>
          These Terms are governed by the laws of <strong>India</strong>. Courts located in <strong>Mumbai, Maharashtra</strong> shall have exclusive jurisdiction.
        </p>

        <h2 style={styles.subheading}>14) Changes</h2>
        <p style={styles.text}>
          We may update these Terms from time to time. The version in effect at the time of your purchase governs that purchase.
        </p>

        <h2 style={styles.subheading}>15) Contact</h2>
        <p style={styles.text}>
          Tender Managers · Email: <a href="mailto:info@tendermanagers.com">info@tendermanagers.com</a> ·
          Phone/WhatsApp: <a href="tel:+918655654720">+91-8655654720</a> ·
          WhatsApp: <a href="https://wa.me/918655654720" target="_blank" rel="noreferrer">wa.me/918655654720</a>
        </p>

        <hr style={styles.hr} />

        <p style={styles.smallNote}>
          <strong>Checkout notice suggestion:</strong> “By proceeding, you agree to Tender Managers’ Reports License & Terms.
          Internal use only. Redistribution/resale prohibited.”
        </p>
      </div>
    </Layout>
  );
};

const styles = {
  pageContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px',
    textAlign: 'left',
  },
  heading: { fontSize: '28px', marginBottom: '16px' },
  subheading: { fontSize: '20px', marginTop: '18px', marginBottom: '8px' },
  minorHeading: { fontSize: '16px', marginTop: '10px', marginBottom: '6px' },
  text: { fontSize: '16px', marginBottom: '10px', lineHeight: 1.55 },
  list: { margin: '6px 0 12px 20px', lineHeight: 1.55 },
  hr: { border: 0, height: 1, background: '#e5e7eb', margin: '20px 0' },
  smallNote: { fontSize: 13, color: '#4b5563' },
};

export default TermsAndConditions;
